import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Text,
  Button,
  Input,
  Select,
  Checkbox,
  HStack,
  Link,
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { replaceApplyFormData, saveApplyFormData, loadApplyFormData } from "./applyFormStorage";
import { saveUserSession } from "./userSessionStorage";

export default function ApplyPreliminaryPage() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8001";
  const [values, setValues] = useState({
    loanAmount: "",
    province: "",
    email: "",
    password: "",
    marketingConsent: false,
    termsConsent: false,
  });

  // Restore form values from localStorage on mount
  useEffect(() => {
    const saved = loadApplyFormData();
    if (saved && saved.preliminary) {
      setValues((prev) => ({ ...prev, ...saved.preliminary }));
    }
  }, []);
  const [touched, setTouched] = useState({
    loanAmount: false,
    province: false,
    email: false,
    password: false,
    termsConsent: false,
  });
  const [focusedField, setFocusedField] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const loanAmountOptions = [];

  for (let amount = 100; amount <= 1500; amount += 50) {
    loanAmountOptions.push(amount);
  }

  for (let amount = 2000; amount <= 20000; amount += 500) {
    loanAmountOptions.push(amount);
  }

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email);
  const passwordIsValid = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(values.password);

  const errors = {
    loanAmount: touched.loanAmount && !values.loanAmount ? "Please select a loan amount" : "",
    province: touched.province && !values.province ? "Province is required" : "",
    email:
      touched.email && !values.email
        ? "Email is required"
        : touched.email && values.email && !emailIsValid
        ? "Enter a valid email address"
        : "",
    password:
      touched.password && !values.password
        ? "Password is required"
        : touched.password && values.password && !passwordIsValid
        ? "Password must be at least 8 characters and include letters and numbers"
        : "",
    termsConsent: touched.termsConsent && !values.termsConsent ? "You must agree to continue our terms & conditions and privacy policy" : "",
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const setAllTouched = () => {
    setTouched({
      loanAmount: true,
      province: true,
      email: true,
      password: true,
      termsConsent: true,
    });
  };

  const handleChange = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleFocus = (field) => () => {
    setFocusedField(field);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFocusedField((prev) => (prev === field ? "" : prev));
  };

  const isFloating = (field) => focusedField === field || values[field] !== "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    setAllTouched();
    if (
      !values.loanAmount ||
      !values.province ||
      !values.email ||
      !values.password ||
      !values.termsConsent ||
      !emailIsValid ||
      !passwordIsValid ||
      hasErrors
    ) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/api/customer/register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-App": "swiftcredit",
        },
        body: JSON.stringify({
          loanAmount: values.loanAmount,
          province: values.province,
          email: values.email,
          password: values.password,
          marketingConsent: values.marketingConsent,
          termsConsent: values.termsConsent,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        setSubmitError(payload.message || "Unable to create profile right now. Please try again.");
        return;
      }

      const sessionAuth = {
        isAuthenticated: true,
        customerId: payload.customer?.id || null,
        email: payload.customer?.email || values.email,
      };

      replaceApplyFormData({
        preliminary: {
          loanAmount: values.loanAmount,
          province: values.province,
          email: values.email,
          marketingConsent: values.marketingConsent,
          termsConsent: values.termsConsent,
        },
        auth: sessionAuth,
        customer: {},
        income: {},
        bank: {},
        flow: {
          personalInfoBackPath: "/apply",
        },
      });

      saveUserSession({
        auth: sessionAuth,
        customer: payload.customer || {
          id: sessionAuth.customerId,
          email: sessionAuth.email,
        },
      });

      navigate("/apply/personal-info");
    } catch {
      setSubmitError("Network error while creating your profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="xl" px={{ base: 4, md: 5 }} py={{ base: 4, md: 4 }}>
              <VStack spacing={3}>
                <Text color="gray.700" fontWeight="600" textAlign="center">
                  Do you already have an account with us? If so, please sign in.
                </Text>
                <Button as={RouterLink} to="/apply/sign-in" bg="white" color="black" border="1px solid" borderColor="gray.300" borderRadius="full" px={7} h="44px" _hover={{ bg: "gray.100" }}>
                  Sign in
                </Button>
              </VStack>
        </Box>
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={Boolean(errors.loanAmount)} position="relative">
                  <FormLabel
                    position="absolute"
                    left="12px"
                    top={isFloating("loanAmount") ? "-9px" : "13px"}
                    zIndex={2}
                    px={2}
                    bg="white"
                    fontSize={isFloating("loanAmount") ? "xs" : "sm"}
                    color={isFloating("loanAmount") ? "gray.700" : "gray.500"}
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    m={0}
                  >
                    How much loan do you need?
                  </FormLabel>
                  <Select
                    value={values.loanAmount}
                    onChange={handleChange("loanAmount")}
                    onFocus={handleFocus("loanAmount")}
                    onBlur={handleBlur("loanAmount")}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    required
                  >
                    <option value="" disabled hidden />
                    {loanAmountOptions.map((amount) => (
                      <option key={amount} value={amount}>
                        {`$${amount.toLocaleString()}`}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.loanAmount}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.province)} position="relative">
                  <FormLabel
                    position="absolute"
                    left="12px"
                    top={isFloating("province") ? "-9px" : "13px"}
                    zIndex={2}
                    px={2}
                    bg="white"
                    fontSize={isFloating("province") ? "xs" : "sm"}
                    color={isFloating("province") ? "gray.700" : "gray.500"}
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    m={0}
                  >
                    Province
                  </FormLabel>
                  <Select
                    value={values.province}
                    onChange={handleChange("province")}
                    onFocus={handleFocus("province")}
                    onBlur={handleBlur("province")}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    required
                  >
                    <option value="" disabled hidden />
                    <option value="Ontario">Ontario</option>
                    <option value="Alberta">Alberta</option>
                    <option value="British Columbia">British Columbia</option>
                    <option value="Manitoba">Manitoba</option>
                    <option value="Saskatchewan">Saskatchewan</option>
                    <option value="Nova Scotia">Nova Scotia</option>
                    <option value="New Brunswick">New Brunswick</option>
                    <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                  </Select>
                  <FormErrorMessage>{errors.province}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.email)} position="relative">
                  <FormLabel
                    position="absolute"
                    left="12px"
                    top={isFloating("email") ? "-9px" : "13px"}
                    zIndex={2}
                    px={2}
                    bg="white"
                    fontSize={isFloating("email") ? "xs" : "sm"}
                    color={isFloating("email") ? "gray.700" : "gray.500"}
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    m={0}
                  >
                    Email (this will also be your username)
                  </FormLabel>
                  <Input
                    type="email"
                    value={values.email}
                    onChange={handleChange("email")}
                    onFocus={handleFocus("email")}
                    onBlur={handleBlur("email")}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    placeholder=""
                    required
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.password)} position="relative">
                  <FormLabel
                    position="absolute"
                    left="12px"
                    top={isFloating("password") ? "-9px" : "13px"}
                    zIndex={2}
                    px={2}
                    bg="white"
                    fontSize={isFloating("password") ? "xs" : "sm"}
                    color={isFloating("password") ? "gray.700" : "gray.500"}
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    m={0}
                  >
                    Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={handleChange("password")}
                      onFocus={handleFocus("password")}
                      onBlur={handleBlur("password")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <InputRightElement h="52px">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowPassword((prev) => !prev)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <HStack align="flex-start" spacing={3}>
                    <Checkbox
                      mt="1"
                      colorScheme="blackAlpha"
                      isChecked={values.marketingConsent}
                      onChange={(event) => setValues((prev) => ({ ...prev, marketingConsent: event.target.checked }))}
                    />
                    <Text fontSize="sm" color="gray.700">
                      I want to receive exclusive offers, financial tips, and updates from Swift Credit. I can unsubscribe anytime.
                    </Text>
                  </HStack>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.termsConsent)}>
                  <HStack align="flex-start" spacing={3}>
                    <Checkbox
                      mt="1"
                      colorScheme="blackAlpha"
                      isChecked={values.termsConsent}
                      onChange={(event) => setValues((prev) => ({ ...prev, termsConsent: event.target.checked }))}
                      onBlur={handleBlur("termsConsent")}
                    />
                    <Text fontSize="sm" color="gray.700">
                      I confirm that I have read and agree to our {" "}
                      <Link
                        as={RouterLink}
                        to="/terms-of-service"
                        color="blue.500"
                        textDecoration="underline"
                        _hover={{ color: "blue.600" }}
                        onClick={() => {
                          if (typeof saveApplyFormData === 'function') {
                            saveApplyFormData({ preliminary: values });
                          }
                        }}
                      >
                        terms & conditions
                      </Link>{" "}
                      and {" "}
                      <Link
                        as={RouterLink}
                        to="/privacy-policy"
                        color="blue.500"
                        textDecoration="underline"
                        _hover={{ color: "blue.600" }}
                        onClick={() => {
                          if (typeof saveApplyFormData === 'function') {
                            saveApplyFormData({ preliminary: values });
                          }
                        }}
                      >
                        privacy policy
                      </Link>
                    </Text>
                  </HStack>
                  <FormErrorMessage>{errors.termsConsent}</FormErrorMessage>
                </FormControl>

                {submitError && (
                  <Text color="red.500" fontSize="sm">
                    {submitError}
                  </Text>
                )}

                <Button type="submit" isLoading={isSubmitting} loadingText="Creating profile" bg="black" color="white" borderRadius="full" h="50px" _hover={{ bg: "gray.900" }}>
                  Confirm
                </Button>
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
